import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Textarea } from "../../components/ui/textarea";
import { Progress } from "../../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Separator } from "../../components/ui/separator";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { 
  FileText, Calendar, Users, ChevronRight, ChevronLeft,
  CheckCircle2, Loader2, Download, Eye, Save, Building2, User,
  TrendingUp, TrendingDown, MapPin, FileEdit, UserPlus, UserMinus,
  RefreshCw, XCircle, ArrowLeftRight, MoreHorizontal, Sparkles, ArrowLeft,
  Mail, AlertTriangle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Icon mapping for AGE types
const ICON_MAP = {
  TrendingUp, TrendingDown, MapPin, FileEdit, UserPlus, UserMinus,
  RefreshCw, XCircle, ArrowLeftRight, MoreHorizontal
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(amount || 0);
};

export default function GenerateurAGE() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  
  // State
  const [ageTypes, setAgeTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [wizardData, setWizardData] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    date_age: "",
    heure_age: "10h00",
    heure_fin: "11h00",
    date_convocation: "",
    quorum_requis: 66,
    secretaire_id: null,
    secretaire_externe: "",
    presences: {},
    resolution_data: {}
  });
  
  // Auto-format date input (adds / automatically) with validation
  const handleDateChange = (field, value) => {
    // Remove any non-digit characters except /
    let cleaned = value.replace(/[^\d/]/g, '');
    
    // Remove existing slashes to reformat
    const digits = cleaned.replace(/\//g, '');
    
    // Format as JJ/MM/AAAA
    let formatted = '';
    for (let i = 0; i < digits.length && i < 8; i++) {
      if (i === 2 || i === 4) {
        formatted += '/';
      }
      formatted += digits[i];
    }
    
    // If date_age is being updated and we have a complete date, auto-calculate convocation date
    if (field === "date_age" && formatted.length === 10) {
      const parts = formatted.split('/');
      if (parts.length === 3) {
        const ageDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
        if (!isNaN(ageDate.getTime())) {
          // Convocation must be at least 20 days before AGE
          const convocationDate = new Date(ageDate);
          convocationDate.setDate(convocationDate.getDate() - 20);
          const convFormatted = `${String(convocationDate.getDate()).padStart(2, '0')}/${String(convocationDate.getMonth() + 1).padStart(2, '0')}/${convocationDate.getFullYear()}`;
          
          setFormData(prev => ({
            ...prev,
            [field]: formatted,
            date_convocation: convFormatted
          }));
          setConvocationError(null);
          return;
        }
      }
    }
    
    // If date_convocation is being updated, validate it's at least 20 days before AGE
    if (field === "date_convocation" && formatted.length === 10 && formData.date_age?.length === 10) {
      const convParts = formatted.split('/');
      const ageParts = formData.date_age.split('/');
      
      if (convParts.length === 3 && ageParts.length === 3) {
        const convDate = new Date(parseInt(convParts[2]), parseInt(convParts[1]) - 1, parseInt(convParts[0]));
        const ageDate = new Date(parseInt(ageParts[2]), parseInt(ageParts[1]) - 1, parseInt(ageParts[0]));
        
        if (!isNaN(convDate.getTime()) && !isNaN(ageDate.getTime())) {
          const diffDays = Math.floor((ageDate - convDate) / (1000 * 60 * 60 * 24));
          
          if (diffDays < 20) {
            setConvocationError(`La date de convocation doit être au minimum 20 jours avant l'AGE (actuellement ${diffDays} jours)`);
          } else {
            setConvocationError(null);
          }
        }
      }
    }
    
    setFormData(prev => ({ ...prev, [field]: formatted }));
  };
  
  // Generate convocation letters for AGE
  const generateConvocations = async () => {
    if (!formData.date_age || !formData.date_convocation) {
      toast.error("Veuillez d'abord renseigner les dates de l'AGE et de convocation");
      return;
    }
    
    setGeneratingConvocations(true);
    try {
      const res = await axios.post(`${API}/api/corporate/age/convocations/generate`, {
        type_age: selectedType?.id,
        type_label: selectedType?.label,
        date_age: formData.date_age,
        heure_age: formData.heure_age || "10h00",
        date_convocation: formData.date_convocation,
        ordre_du_jour: selectedType?.label ? [selectedType.label, "Questions diverses"] : undefined
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeneratedConvocations(res.data.convocations || []);
      toast.success(res.data.message || "Lettres de convocation générées !");
      
    } catch (error) {
      console.error("Error generating convocations:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération des convocations");
    } finally {
      setGeneratingConvocations(false);
    }
  };
  
  // Download a convocation PDF
  const downloadConvocation = async (convId, filename) => {
    try {
      const res = await axios.get(`${API}/api/corporate/age/convocations/${convId}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };
  
  // Preview
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Convocation state
  const [convocationError, setConvocationError] = useState(null);
  const [generatingConvocations, setGeneratingConvocations] = useState(false);
  const [generatedConvocations, setGeneratedConvocations] = useState([]);

  // Fetch AGE types
  const fetchTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/corporate/age/types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgeTypes(res.data.types || []);
    } catch (error) {
      console.error("Error fetching AGE types:", error);
      toast.error("Erreur lors du chargement des types d'AGE");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  // Initialize wizard for selected type
  const initWizard = async (ageType) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/corporate/age/wizard/init`, {
        params: { age_type: ageType },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setWizardData(res.data);
      setSelectedType(res.data.age_type_config);
      
      // Pre-fill form
      const defaults = res.data.default_values;
      const typeDefaults = res.data.type_defaults || {};
      const draft = res.data.existing_draft?.data;
      
      setFormData(prev => ({
        ...prev,
        date_age: draft?.date_age || defaults.date_age,
        heure_age: draft?.heure_age || defaults.heure_age,
        heure_fin: draft?.heure_fin || defaults.heure_fin,
        date_convocation: draft?.date_convocation || defaults.date_convocation,
        presences: draft?.presences || {},
        resolution_data: draft?.resolution_data || typeDefaults
      }));
      
      setCurrentStep(1);
    } catch (error) {
      console.error("Error initializing wizard:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'initialisation");
    } finally {
      setLoading(false);
    }
  };

  // Update resolution data
  const updateResolutionData = (field, value) => {
    setFormData(prev => ({
      ...prev,
      resolution_data: { ...prev.resolution_data, [field]: value }
    }));
  };

  // Update presence
  const updatePresence = (associeId, presence) => {
    setFormData(prev => ({
      ...prev,
      presences: { ...prev.presences, [associeId]: presence }
    }));
  };

  // Save draft
  const saveDraft = async () => {
    try {
      await axios.post(`${API}/api/corporate/age/draft`, {
        age_type: selectedType.id,
        data: formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Brouillon enregistré");
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
  };

  // Generate preview
  const generatePreview = async () => {
    setPreviewLoading(true);
    try {
      const res = await axios.post(`${API}/api/corporate/age/generate/preview`, {
        age_type: selectedType.id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      setPreviewUrl(url);
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("Erreur lors de la génération de l'aperçu");
    } finally {
      setPreviewLoading(false);
    }
  };

  // Generate final
  const generateFinal = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/api/corporate/age/generate/final`, {
        age_type: selectedType.id,
        ...formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("PV AGE généré et enregistré dans le Coffre-Fort !");
      
      // Reset
      setSelectedType(null);
      setCurrentStep(0);
      setFormData({
        date_age: "",
        heure_age: "10h00",
        heure_fin: "11h00",
        date_convocation: "",
        quorum_requis: 66,
        secretaire_id: null,
        presences: {},
        resolution_data: {}
      });
    } catch (error) {
      console.error("Error generating final:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de la génération");
    } finally {
      setGenerating(false);
    }
  };

  // Navigation
  const nextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
      if (currentStep === 3) {
        generatePreview();
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const backToTypeSelection = () => {
    setSelectedType(null);
    setCurrentStep(0);
    setWizardData(null);
  };

  const getIcon = (iconName) => {
    return ICON_MAP[iconName] || FileText;
  };

  if (loading && !selectedType) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Step 0: Type Selection
  if (!selectedType) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl shadow-lg">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Génération AGE</h2>
            <p className="text-sm text-gray-500">Assemblée Générale Extraordinaire</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Choisir le type d'AGE</CardTitle>
            <CardDescription>Sélectionnez le motif de l'assemblée extraordinaire</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {ageTypes.map((type) => {
                const Icon = getIcon(type.icon);
                return (
                  <Card 
                    key={type.id}
                    className="cursor-pointer hover:shadow-md transition-all hover:border-indigo-300"
                    onClick={() => initWizard(type.id)}
                    data-testid={`age-type-${type.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="p-2 bg-indigo-100 rounded-lg">
                          <Icon className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div className="flex-1">
                          <p className="font-semibold text-gray-900">{type.name}</p>
                          <p className="text-sm text-gray-500 mt-1">{type.description}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Wizard Steps
  const STEPS = [
    { id: "type", title: "Type" },
    { id: "date", title: "Date" },
    { id: "resolution", title: "Résolution" },
    { id: "participants", title: "Participants" },
    { id: "preview", title: "Aperçu" }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={backToTypeSelection}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="p-3 bg-gradient-to-br from-red-500 to-orange-600 rounded-xl shadow-lg">
            {(() => {
              const Icon = getIcon(selectedType.icon);
              return <Icon className="w-6 h-6 text-white" />;
            })()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">{selectedType.name}</h2>
            <p className="text-sm text-gray-500">Assemblée Générale Extraordinaire</p>
          </div>
        </div>
        
        <Button variant="outline" onClick={saveDraft} className="gap-2">
          <Save className="w-4 h-4" />
          Sauvegarder
        </Button>
      </div>

      {/* Progress */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-2">
          {STEPS.slice(1).map((step, idx) => {
            const stepNum = idx + 1;
            const isActive = stepNum === currentStep;
            const isCompleted = stepNum < currentStep;
            
            return (
              <div 
                key={step.id}
                className={`flex items-center ${idx < STEPS.length - 2 ? 'flex-1' : ''}`}
              >
                <div 
                  className={`flex items-center gap-2 ${
                    isActive ? 'text-indigo-600' : isCompleted ? 'text-green-600' : 'text-gray-400'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    isActive ? 'bg-indigo-100' : isCompleted ? 'bg-green-100' : 'bg-gray-100'
                  }`}>
                    {isCompleted ? <CheckCircle2 className="w-5 h-5" /> : stepNum}
                  </div>
                  <span className="hidden md:block text-sm font-medium">{step.title}</span>
                </div>
                
                {idx < STEPS.length - 2 && (
                  <div className={`flex-1 h-0.5 mx-2 ${isCompleted ? 'bg-green-300' : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>
        <Progress value={(currentStep / 4) * 100} className="h-1" />
      </div>

      {/* Step Content */}
      <Card>
        <CardContent className="p-6">
          {/* Step 1: Date */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Date et heure de l'AGE</h3>
                <p className="text-gray-500">Définissez quand se tiendra l'assemblée</p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto">
                <div className="space-y-2">
                  <Label>Date de l'AGE *</Label>
                  <Input
                    type="text"
                    value={formData.date_age}
                    onChange={(e) => handleDateChange("date_age", e.target.value)}
                    placeholder="JJ/MM/AAAA"
                    maxLength={10}
                    data-testid="date-age-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Date de convocation</Label>
                  <Input
                    type="text"
                    value={formData.date_convocation}
                    onChange={(e) => handleDateChange("date_convocation", e.target.value)}
                    placeholder="JJ/MM/AAAA"
                    maxLength={10}
                    className={convocationError ? "border-red-500" : ""}
                  />
                  {convocationError ? (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      {convocationError}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500">
                      Minimum 20 jours avant l'AGE (délai légal)
                    </p>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Heure de début</Label>
                  <Input
                    type="text"
                    value={formData.heure_age}
                    onChange={(e) => setFormData(prev => ({ ...prev, heure_age: e.target.value }))}
                    placeholder="10h00"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Heure de fin</Label>
                  <Input
                    type="text"
                    value={formData.heure_fin}
                    onChange={(e) => setFormData(prev => ({ ...prev, heure_fin: e.target.value }))}
                    placeholder="11h00"
                  />
                </div>
              </div>
              
              {/* Convocation Letters Section */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <h4 className="font-medium text-amber-800">Lettres de convocation</h4>
                    <p className="text-sm text-amber-700 mt-1">
                      Générez automatiquement les lettres de convocation pour chaque associé (délai légal: 20 jours avant l'AGE)
                    </p>
                    
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        onClick={generateConvocations}
                        disabled={generatingConvocations || !formData.date_age || !formData.date_convocation || convocationError}
                        className="gap-2 border-amber-300 hover:bg-amber-100"
                      >
                        {generatingConvocations ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Mail className="w-4 h-4" />
                        )}
                        Générer les convocations
                      </Button>
                    </div>
                    
                    {/* List of generated convocations */}
                    {generatedConvocations.length > 0 && (
                      <div className="mt-4 space-y-2">
                        <p className="text-sm font-medium text-amber-800">
                          {generatedConvocations.length} convocation(s) générée(s) :
                        </p>
                        {generatedConvocations.map((conv) => (
                          <div key={conv.id} className="flex items-center justify-between bg-white p-2 rounded border border-amber-200">
                            <span className="text-sm text-gray-700">{conv.associe_nom}</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => downloadConvocation(conv.id, conv.filename)}
                              className="gap-1 text-amber-700 hover:text-amber-800"
                            >
                              <Download className="w-4 h-4" />
                              PDF
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Resolution Details */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Détails de la résolution</h3>
                <p className="text-gray-500">{selectedType.name}</p>
              </div>
              
              {/* Type-specific forms */}
              {selectedType.id === "augmentation_capital" && (
                <AugmentationCapitalForm 
                  data={formData.resolution_data} 
                  onChange={updateResolutionData}
                  company={wizardData?.company}
                />
              )}
              
              {selectedType.id === "transfert_siege" && (
                <TransfertSiegeForm 
                  data={formData.resolution_data} 
                  onChange={updateResolutionData}
                  company={wizardData?.company}
                />
              )}
              
              {selectedType.id === "nomination_dirigeant" && (
                <NominationDirigeantForm 
                  data={formData.resolution_data} 
                  onChange={updateResolutionData}
                  company={wizardData?.company}
                />
              )}
              
              {selectedType.id === "cession_parts" && (
                <CessionPartsForm 
                  data={formData.resolution_data} 
                  onChange={updateResolutionData}
                  associes={wizardData?.associes}
                />
              )}
              
              {/* Generic form for other types */}
              {!["augmentation_capital", "transfert_siege", "nomination_dirigeant", "cession_parts"].includes(selectedType.id) && (
                <GenericResolutionForm 
                  data={formData.resolution_data} 
                  onChange={updateResolutionData}
                  ageType={selectedType}
                />
              )}
            </div>
          )}

          {/* Step 3: Participants */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Participants</h3>
                <p className="text-gray-500">Confirmez la présence des associés (quorum AGE: {formData.quorum_requis}%)</p>
              </div>
              
              <div className="space-y-4">
                {wizardData?.associes?.map((associe) => (
                  <div 
                    key={associe.id}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        {associe.type === "personne_morale" ? (
                          <Building2 className="w-5 h-5 text-purple-600" />
                        ) : (
                          <User className="w-5 h-5 text-purple-600" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {associe.type === "personne_morale" 
                            ? associe.denomination 
                            : `${associe.civilite || ""} ${associe.prenom || ""} ${associe.nom || ""}`
                          }
                        </p>
                        <p className="text-sm text-gray-500">
                          {associe.nombre_parts} parts ({associe.pourcentage}%)
                        </p>
                      </div>
                    </div>
                    
                    <Select 
                      value={formData.presences[associe.id] || "Présent"}
                      onValueChange={(v) => updatePresence(associe.id, v)}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Présent">Présent</SelectItem>
                        <SelectItem value="Représenté">Représenté</SelectItem>
                        <SelectItem value="Absent">Absent</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 4: Preview */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Aperçu du PV d'AGE</h3>
                <p className="text-gray-500">Vérifiez le document avant génération finale</p>
              </div>
              
              {previewLoading ? (
                <div className="flex flex-col items-center justify-center h-64">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mb-3" />
                  <p className="text-gray-500">Génération de l'aperçu...</p>
                </div>
              ) : previewUrl ? (
                <div className="border rounded-lg overflow-hidden" style={{ height: "500px" }}>
                  <iframe
                    src={previewUrl}
                    className="w-full h-full"
                    title="Aperçu PV AGE"
                  />
                </div>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>Impossible de charger l'aperçu</p>
                  <Button variant="outline" onClick={generatePreview} className="mt-4">
                    Réessayer
                  </Button>
                </div>
              )}
              
              <div className="flex items-center justify-center gap-4 pt-4 border-t">
                <Button variant="outline" onClick={generatePreview} disabled={previewLoading}>
                  <Eye className="w-4 h-4 mr-2" />
                  Rafraîchir
                </Button>
                
                <Button 
                  onClick={generateFinal} 
                  disabled={generating}
                  className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 gap-2"
                  data-testid="generate-final-btn"
                >
                  {generating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  Générer et enregistrer
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button 
          variant="outline" 
          onClick={prevStep}
          disabled={currentStep <= 1}
          className="gap-2"
        >
          <ChevronLeft className="w-4 h-4" />
          Précédent
        </Button>
        
        {currentStep < 4 && (
          <Button 
            onClick={nextStep} 
            className="gap-2" 
            data-testid="next-step-btn"
            disabled={currentStep === 2 && convocationError}
          >
            Suivant
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Type-specific Forms
// ============================================================================

function AugmentationCapitalForm({ data, onChange, company }) {
  const capitalActuel = company?.capital_social || 0;
  const partsActuelles = company?.nombre_parts || 0;
  const valeurNominale = company?.valeur_nominale || 10;
  
  const montantAugmentation = parseFloat(data.montant_augmentation) || 0;
  const nouveauCapital = capitalActuel + montantAugmentation;
  const nouvellesParts = valeurNominale > 0 ? Math.floor(montantAugmentation / valeurNominale) : 0;
  
  useEffect(() => {
    onChange("nouveau_capital", nouveauCapital);
    onChange("nouvelles_parts", nouvellesParts);
    onChange("nouvelles_parts_total", partsActuelles + nouvellesParts);
    onChange("valeur_nominale", valeurNominale);
  }, [montantAugmentation, nouveauCapital, nouvellesParts, partsActuelles, valeurNominale]);

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="p-4 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Capital actuel :</strong> {formatCurrency(capitalActuel)} 
          ({partsActuelles} parts de {valeurNominale}€)
        </p>
      </div>
      
      <div className="space-y-2">
        <Label>Type d'augmentation</Label>
        <RadioGroup 
          value={data.type_augmentation || "numeraire"}
          onValueChange={(v) => onChange("type_augmentation", v)}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="numeraire" id="numeraire" />
            <Label htmlFor="numeraire">Apports en numéraire (espèces)</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="nature" id="nature" />
            <Label htmlFor="nature">Apports en nature</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="reserves" id="reserves" />
            <Label htmlFor="reserves">Incorporation de réserves</Label>
          </div>
        </RadioGroup>
      </div>
      
      <div className="space-y-2">
        <Label>Montant de l'augmentation *</Label>
        <div className="relative">
          <Input
            type="number"
            value={data.montant_augmentation || ""}
            onChange={(e) => onChange("montant_augmentation", parseFloat(e.target.value) || 0)}
            className="pr-10"
            data-testid="montant-augmentation-input"
          />
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
        </div>
      </div>
      
      {data.type_augmentation === "nature" && (
        <div className="space-y-2">
          <Label>Description de l'apport en nature</Label>
          <Textarea
            value={data.description_apport || ""}
            onChange={(e) => onChange("description_apport", e.target.value)}
            placeholder="Décrivez l'apport en nature..."
            rows={3}
          />
        </div>
      )}
      
      <div className="p-4 bg-green-50 rounded-lg border border-green-200">
        <p className="font-semibold text-green-800">Après augmentation :</p>
        <p className="text-green-700">
          Capital : {formatCurrency(nouveauCapital)} ({partsActuelles + nouvellesParts} parts)
        </p>
        <p className="text-sm text-green-600">
          Création de {nouvellesParts} parts nouvelles
        </p>
      </div>
    </div>
  );
}

function TransfertSiegeForm({ data, onChange, company }) {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="p-4 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-600">
          <strong>Adresse actuelle :</strong><br />
          {company?.siege_social?.adresse}<br />
          {company?.siege_social?.code_postal} {company?.siege_social?.ville}
        </p>
      </div>
      
      <Separator />
      
      <div className="space-y-4">
        <h4 className="font-semibold text-gray-900">Nouvelle adresse</h4>
        
        <div className="space-y-2">
          <Label>Adresse *</Label>
          <Input
            value={data.nouvelle_adresse || ""}
            onChange={(e) => onChange("nouvelle_adresse", e.target.value)}
            placeholder="123 rue de la Nouvelle Adresse"
            data-testid="nouvelle-adresse-input"
          />
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Code postal *</Label>
            <Input
              value={data.nouveau_code_postal || ""}
              onChange={(e) => onChange("nouveau_code_postal", e.target.value)}
              placeholder="75001"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Ville *</Label>
            <Input
              value={data.nouvelle_ville || ""}
              onChange={(e) => onChange("nouvelle_ville", e.target.value)}
              placeholder="Paris"
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label>Date d'effet</Label>
          <Input
            value={data.date_effet || ""}
            onChange={(e) => onChange("date_effet", e.target.value)}
            placeholder="Immédiat ou date spécifique"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Article des statuts relatif au siège</Label>
          <Input
            value={data.article_siege || "4"}
            onChange={(e) => onChange("article_siege", e.target.value)}
            placeholder="4"
          />
        </div>
      </div>
    </div>
  );
}

function NominationDirigeantForm({ data, onChange, company }) {
  const fonction = company?.forme_juridique?.includes("SAS") ? "Président" : "Gérant";
  
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="space-y-2">
        <Label>Fonction</Label>
        <Select 
          value={data.fonction_dirigeant || fonction}
          onValueChange={(v) => onChange("fonction_dirigeant", v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="Président">Président</SelectItem>
            <SelectItem value="Directeur Général">Directeur Général</SelectItem>
            <SelectItem value="Gérant">Gérant</SelectItem>
            <SelectItem value="Co-gérant">Co-gérant</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      <Separator />
      
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Civilité</Label>
          <Select 
            value={data.civilite_nouveau || "M."}
            onValueChange={(v) => onChange("civilite_nouveau", v)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="M.">M.</SelectItem>
              <SelectItem value="Mme">Mme</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Prénom *</Label>
          <Input
            value={data.prenom_nouveau || ""}
            onChange={(e) => onChange("prenom_nouveau", e.target.value)}
            placeholder="Jean"
            data-testid="prenom-nouveau-input"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Nom *</Label>
          <Input
            value={data.nom_nouveau || ""}
            onChange={(e) => onChange("nom_nouveau", e.target.value)}
            placeholder="DUPONT"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date de naissance</Label>
          <Input
            value={data.date_naissance_nouveau || ""}
            onChange={(e) => onChange("date_naissance_nouveau", e.target.value)}
            placeholder="01/01/1980"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Lieu de naissance</Label>
          <Input
            value={data.lieu_naissance_nouveau || ""}
            onChange={(e) => onChange("lieu_naissance_nouveau", e.target.value)}
            placeholder="Paris"
          />
        </div>
      </div>
      
      <div className="space-y-2">
        <Label>Adresse</Label>
        <Input
          value={data.adresse_nouveau || ""}
          onChange={(e) => onChange("adresse_nouveau", e.target.value)}
          placeholder="123 rue..., 75001 Paris"
        />
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Date d'effet</Label>
          <Input
            value={data.date_effet || ""}
            onChange={(e) => onChange("date_effet", e.target.value)}
            placeholder="Immédiat"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Durée du mandat</Label>
          <Input
            value={data.duree_mandat || ""}
            onChange={(e) => onChange("duree_mandat", e.target.value)}
            placeholder="Durée illimitée"
          />
        </div>
      </div>
    </div>
  );
}

function CessionPartsForm({ data, onChange, associes }) {
  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Cédant (vendeur)</Label>
          <Select 
            value={data.cedant_id || ""}
            onValueChange={(v) => {
              onChange("cedant_id", v);
              const cedant = associes?.find(a => a.id === v);
              if (cedant) {
                const name = cedant.type === "personne_morale" 
                  ? cedant.denomination 
                  : `${cedant.civilite || ""} ${cedant.prenom || ""} ${cedant.nom || ""}`.trim();
                onChange("cedant_nom", name);
              }
            }}
          >
            <SelectTrigger data-testid="cedant-select">
              <SelectValue placeholder="Sélectionner" />
            </SelectTrigger>
            <SelectContent>
              {associes?.map(a => (
                <SelectItem key={a.id} value={a.id}>
                  {a.type === "personne_morale" 
                    ? a.denomination 
                    : `${a.civilite || ""} ${a.prenom || ""} ${a.nom || ""}`
                  } ({a.nombre_parts} parts)
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        <div className="space-y-2">
          <Label>Cessionnaire (acheteur)</Label>
          <Input
            value={data.cessionnaire_nom || ""}
            onChange={(e) => onChange("cessionnaire_nom", e.target.value)}
            placeholder="Nom du cessionnaire"
          />
        </div>
      </div>
      
      <div className="grid grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label>Nombre de parts cédées *</Label>
          <Input
            type="number"
            value={data.nombre_parts_cedees || ""}
            onChange={(e) => onChange("nombre_parts_cedees", parseInt(e.target.value) || 0)}
            placeholder="100"
          />
        </div>
        
        <div className="space-y-2">
          <Label>Prix de cession *</Label>
          <div className="relative">
            <Input
              type="number"
              value={data.prix_cession || ""}
              onChange={(e) => onChange("prix_cession", parseFloat(e.target.value) || 0)}
              className="pr-10"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">€</span>
          </div>
        </div>
      </div>
    </div>
  );
}

// Templates spécifiques par type d'AGE
const RESOLUTION_TEMPLATES = {
  reduction_capital: {
    ordre_du_jour: [
      {
        label: "Réduction motivée par des pertes",
        description: "Apurement des pertes constatées au bilan",
        items: [
          "Rapport du Gérant/Président sur les motifs de la réduction de capital",
          "Rapport du Commissaire aux comptes, le cas échéant",
          "Réduction du capital social motivée par des pertes",
          "Modification corrélative des statuts (article capital social)",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
      {
        label: "Réduction non motivée par des pertes",
        description: "Rachat / remboursement aux associés",
        items: [
          "Rapport du Gérant/Président sur les motifs de la réduction de capital",
          "Rapport du Commissaire aux comptes sur les causes et conditions de la réduction",
          "Réduction du capital social par rachat de parts/actions et remboursement aux associés",
          "Autorisation donnée au Gérant/Président de procéder aux opérations de rachat",
          "Modification corrélative des statuts",
          "Pouvoirs pour formalités",
        ],
      },
    ],
    contenu_resolution: [
      {
        label: "Ex. 1 — Motivée par des pertes",
        description: "SARL, apurement du bilan",
        content:
          "L'Assemblée Générale, après avoir pris connaissance du rapport du Gérant [et du rapport du Commissaire aux comptes], décide de réduire le capital social d'une somme de [montant] euros pour le ramener de [montant initial] euros à [montant final] euros, par voie de réduction de la valeur nominale des parts sociales, motivée par l'existence de pertes constatées au bilan arrêté au [date]. En conséquence, l'article [X] des statuts est modifié comme suit : « Le capital social est fixé à [montant final] euros, divisé en [nombre] parts de [valeur nominale] euros chacune. »",
      },
      {
        label: "Ex. 2 — Rachat & annulation",
        description: "Non motivée par des pertes, avec remboursement",
        content:
          "L'Assemblée Générale décide de réduire le capital social d'une somme de [montant] euros pour le ramener de [montant initial] euros à [montant final] euros, non motivée par des pertes, par voie de rachat de [nombre] parts sociales suivi de leur annulation. Cette réduction donnera lieu au remboursement aux associés concernés d'une somme de [montant] euros par part. Conformément à l'article L.223-34 du Code de commerce, les créanciers pourront former opposition dans un délai de 20 jours à compter du dépôt au greffe du procès-verbal de la présente décision. La réduction ne sera définitive qu'à l'issue de ce délai, sauf constitution de garanties ou remboursement des créances. En conséquence, l'article [X] des statuts est modifié comme suit : [nouvelle rédaction].",
      },
      {
        label: "Ex. 3 — SA / SAS (L.225-204)",
        description: "Société par actions, rapport du CAC",
        content:
          "L'Assemblée Générale, statuant aux conditions de quorum et de majorité des assemblées générales extraordinaires, après lecture des rapports du Conseil et du Commissaire aux comptes établis en application de l'article L.225-204 du Code de commerce, décide de réduire le capital social de [montant initial] à [montant final] euros par voie de [réduction du nombre d'actions / réduction du nominal], et donne tous pouvoirs au Président pour procéder aux formalités de publicité et de dépôt.",
      },
    ],
    date_effet: [
      { label: "Immédiat", value: "Immédiat", hint: "Réduction motivée par des pertes — aucun délai d'opposition requis" },
      {
        label: "À l'issue du délai d'opposition (20 j)",
        value: "À l'issue du délai d'opposition des créanciers de 20 jours à compter du dépôt du procès-verbal au greffe",
        hint: "Obligatoire si réduction non motivée par des pertes (SARL L.223-34 / SA L.225-205)",
      },
    ],
    warning: {
      title: "Point d'attention",
      body: "si la réduction n'est pas motivée par des pertes, le délai d'opposition des créanciers de 20 jours est obligatoire (SARL : art. L.223-34 ; SA : art. L.225-205) — à mentionner explicitement dans la résolution pour éviter tout vice de forme sur la convocation.",
    },
  },

  modification_statuts: {
    ordre_du_jour: [
      {
        label: "Modification de l'objet social",
        description: "Extension ou changement d'activité",
        items: [
          "Rapport du Gérant/Président sur les motifs de la modification de l'objet social",
          "Modification de l'objet social",
          "Modification corrélative de l'article [X] des statuts",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
      {
        label: "Modification de la dénomination sociale",
        description: "Changement de nom",
        items: [
          "Rapport du Gérant/Président sur les motifs du changement de dénomination",
          "Modification de la dénomination sociale",
          "Modification corrélative de l'article [X] des statuts",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
      {
        label: "Modification de la durée",
        description: "Prorogation ou raccourcissement",
        items: [
          "Rapport du Gérant/Président sur les motifs de la prorogation de la durée de la société",
          "Prorogation de la durée de la société",
          "Modification corrélative de l'article [X] des statuts",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
    ],
    contenu_resolution: [
      {
        label: "Objet social",
        description: "Modification de l'article statutaire objet",
        content:
          "L'Assemblée Générale, après avoir pris connaissance du rapport du [Gérant/Président], décide de modifier l'objet social de la société. En conséquence, l'article [X] des statuts est modifié comme suit : « La société a pour objet, en France et à l'étranger : [nouvelle rédaction de l'objet social, activités principales et connexes] et plus généralement toutes opérations industrielles, commerciales, financières, mobilières ou immobilières se rattachant directement ou indirectement à cet objet ou susceptibles d'en favoriser le développement. » Le reste de l'article demeure inchangé.",
      },
      {
        label: "Dénomination sociale",
        description: "Changement de nom + sigle",
        content:
          "L'Assemblée Générale décide de modifier la dénomination sociale de la société qui devient : « [NOUVELLE DÉNOMINATION] » [le cas échéant : ayant pour sigle « [SIGLE] »]. Cette nouvelle dénomination devra figurer sur tous les actes et documents émanant de la société et destinés aux tiers. En conséquence, l'article [X] des statuts est modifié comme suit : « La société a pour dénomination sociale : [NOUVELLE DÉNOMINATION]. Dans tous les actes et documents émanant de la société, cette dénomination doit être précédée ou suivie immédiatement de la mention « [forme juridique] au capital de [montant] euros ». »",
      },
      {
        label: "Prorogation de la durée",
        description: "Extension de la durée de vie sociale",
        content:
          "L'Assemblée Générale, après avoir constaté que la société arrive à échéance le [date d'expiration] et après avoir pris connaissance du rapport du [Gérant/Président], décide de proroger la durée de la société pour une nouvelle période de [nombre] années. La société prendra désormais fin le [nouvelle date d'expiration], sauf prorogation ultérieure ou dissolution anticipée. En conséquence, l'article [X] des statuts est modifié comme suit : « La durée de la société est fixée à [nombre] années à compter de son immatriculation au Registre du commerce et des sociétés, soit jusqu'au [date]. »",
      },
    ],
    date_effet: [
      { label: "Immédiat", value: "Immédiat", hint: "Effet dès le vote de l'assemblée" },
      {
        label: "À l'immatriculation modificative",
        value: "À compter de l'inscription modificative au Registre du commerce et des sociétés",
        hint: "Opposable aux tiers uniquement après publicité RCS",
      },
    ],
    warning: {
      title: "Point d'attention",
      body: "toute modification statutaire nécessite une publication dans un journal d'annonces légales et un dépôt au greffe du tribunal de commerce dans le mois suivant l'AGE (L.210-9). La modification n'est opposable aux tiers qu'à compter de l'inscription modificative au RCS.",
    },
  },

  revocation_dirigeant: {
    ordre_du_jour: [
      {
        label: "Révocation gérant SARL (juste motif)",
        description: "Article L.223-25 — dommages-intérêts si sans juste motif",
        items: [
          "Rapport sur les motifs de la révocation du gérant",
          "Révocation du gérant de ses fonctions",
          "Constatation de la cessation du mandat social",
          "Décision sur le quitus [ou refus de quitus]",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
      {
        label: "Révocation président SAS (ad nutum)",
        description: "Article L.227-5 — révocable à tout moment selon les statuts",
        items: [
          "Révocation du Président de ses fonctions",
          "Constatation de la cessation du mandat social",
          "Décision sur le quitus [ou refus de quitus]",
          "Modalités de nomination du successeur [le cas échéant]",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
    ],
    contenu_resolution: [
      {
        label: "Révocation gérant SARL",
        description: "Avec juste motif, article L.223-25",
        content:
          "L'Assemblée Générale, après avoir pris connaissance des motifs invoqués et conformément à l'article L.223-25 du Code de commerce, décide de révoquer Monsieur/Madame [Nom Prénom], gérant de la société, de ses fonctions à compter du [date d'effet]. La révocation est prononcée pour les motifs suivants : [préciser les justes motifs — manquements graves, faute de gestion, mésentente compromettant l'intérêt social, etc.]. L'Assemblée [donne quitus / refuse le quitus] au gérant révoqué pour sa gestion jusqu'à la date d'effet de la révocation. Tous pouvoirs sont donnés au porteur d'une copie ou d'un extrait du présent procès-verbal pour effectuer les formalités légales de publicité.",
      },
      {
        label: "Révocation président SAS (ad nutum)",
        description: "Conformément aux statuts, sans juste motif requis",
        content:
          "L'Assemblée Générale, conformément aux dispositions de l'article [X] des statuts et de l'article L.227-5 du Code de commerce, décide de révoquer Monsieur/Madame [Nom Prénom], Président de la société, de ses fonctions à compter du [date d'effet]. Cette révocation intervient sans qu'il soit besoin de justifier d'un juste motif, conformément aux statuts. L'Assemblée [donne quitus / refuse le quitus] au Président révoqué pour sa gestion. La Présidence est déclarée vacante à compter de cette même date, dans l'attente de la nomination d'un nouveau Président [ou : la Présidence est confiée à Monsieur/Madame [Nom Prénom] par décision distincte].",
      },
      {
        label: "Révocation DG (SA)",
        description: "Article L.225-55, avec juste motif possible",
        content:
          "L'Assemblée Générale, sur proposition du Conseil d'administration et conformément à l'article L.225-55 du Code de commerce, décide de révoquer Monsieur/Madame [Nom Prénom] de ses fonctions de Directeur Général avec effet au [date]. La révocation est prononcée [pour les motifs suivants : [motifs] / sans que soit fourni de juste motif — dans ce cas, elle pourra ouvrir droit à indemnisation]. L'Assemblée statue également sur le quitus [donné / refusé] pour la gestion jusqu'à cette date.",
      },
    ],
    date_effet: [
      { label: "Immédiat", value: "Immédiat", hint: "Cessation instantanée du mandat" },
      { label: "Date différée", value: "Le [date à préciser]", hint: "Ex. fin de préavis, passation de fonctions" },
    ],
    warning: {
      title: "Attention aux dommages-intérêts",
      body: "gérant SARL / DG SA : la révocation sans juste motif ouvre droit à des dommages-intérêts (L.223-25 / L.225-55). Président SAS : révocation ad nutum sauf clause statutaire contraire (L.227-5). Toujours documenter les motifs au PV pour se prémunir en cas de contentieux.",
    },
  },

  transformation: {
    ordre_du_jour: [
      {
        label: "Transformation SARL → SAS",
        description: "Cas le plus fréquent",
        items: [
          "Rapport du Commissaire à la transformation sur la situation de la société",
          "Rapport du Gérant sur les motifs de la transformation",
          "Transformation de la société en SAS (Société par Actions Simplifiée)",
          "Adoption des nouveaux statuts sous forme de SAS",
          "Cessation des fonctions du/des Gérant(s)",
          "Nomination du Président de la SAS",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
      {
        label: "Transformation SAS → SA",
        description: "Nécessite l'intervention d'un CAC (L.224-3)",
        items: [
          "Rapport du Commissaire à la transformation (art. L.224-3)",
          "Rapport du Président sur les motifs de la transformation",
          "Transformation de la société en SA (Société Anonyme)",
          "Adoption des nouveaux statuts sous forme de SA",
          "Nomination des membres du Conseil d'administration / du Directoire et du Conseil de surveillance",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
      {
        label: "Transformation SCI → SARL",
        description: "Passage d'une société civile à commerciale",
        items: [
          "Rapport du Commissaire à la transformation",
          "Rapport du Gérant sur les motifs de la transformation",
          "Transformation de la société en SARL",
          "Adoption des nouveaux statuts sous forme de SARL",
          "Confirmation ou nomination du/des Gérant(s)",
          "Pouvoirs pour l'accomplissement des formalités",
        ],
      },
    ],
    contenu_resolution: [
      {
        label: "Transformation SARL → SAS",
        description: "Version standard, unanimité des associés requise",
        content:
          "L'Assemblée Générale, statuant à l'unanimité des associés conformément à l'article L.227-3 du Code de commerce, après avoir pris connaissance du rapport du Commissaire à la transformation, décide de transformer la société de sa forme actuelle de Société à Responsabilité Limitée (SARL) en Société par Actions Simplifiée (SAS), à compter du [date]. Cette transformation n'entraîne pas la création d'une personne morale nouvelle. Les parts sociales existantes sont converties en actions de même montant nominal. L'Assemblée adopte en conséquence les nouveaux statuts sous forme de SAS, dont un exemplaire est annexé au présent procès-verbal. Les fonctions du/des Gérant(s) prennent fin de plein droit à compter de la date de la transformation. Monsieur/Madame [Nom Prénom] est nommé(e) Président de la SAS pour une durée [indéterminée / de [X] années].",
      },
      {
        label: "Transformation SAS → SA (L.224-3)",
        description: "Rapport CAC obligatoire, capital minimum 37 000 €",
        content:
          "L'Assemblée Générale, après avoir pris connaissance du rapport du Commissaire à la transformation établi conformément à l'article L.224-3 du Code de commerce attestant que les capitaux propres sont au moins égaux au capital social, décide de transformer la société de sa forme actuelle de SAS en Société Anonyme (SA) à compter du [date]. Cette transformation n'entraîne pas la création d'une personne morale nouvelle. Le capital social, d'un montant de [montant] euros, satisfait au capital minimum requis pour une SA (37 000 euros). Les actions actuelles sont maintenues avec la même valeur nominale. L'Assemblée adopte les nouveaux statuts sous forme de SA et procède à la nomination des membres du [Conseil d'administration / Directoire et Conseil de surveillance].",
      },
      {
        label: "Transformation SCI → SARL",
        description: "Passage société civile → société commerciale",
        content:
          "L'Assemblée Générale, statuant aux conditions requises, après avoir pris connaissance du rapport du Commissaire à la transformation, décide de transformer la société de sa forme actuelle de Société Civile Immobilière (SCI) en Société à Responsabilité Limitée (SARL) à compter du [date]. Cette transformation n'entraîne pas la création d'une personne morale nouvelle et la société conservera sa personnalité morale, son SIREN et son patrimoine. Les parts sociales actuelles sont maintenues, désormais soumises au régime des parts sociales de SARL. L'Assemblée adopte les nouveaux statuts sous forme de SARL, [nomme / confirme dans ses fonctions] Monsieur/Madame [Nom Prénom] en qualité de Gérant.",
      },
    ],
    date_effet: [
      {
        label: "À l'immatriculation modificative",
        value: "À compter de l'inscription modificative au Registre du commerce et des sociétés",
        hint: "Recommandé — opposable aux tiers à cette date",
      },
      { label: "Date précise", value: "Le [date à préciser]", hint: "À condition d'être postérieure à l'AGE et compatible avec les formalités" },
    ],
    warning: {
      title: "Formalités obligatoires",
      body: "toute transformation impose (i) la désignation d'un Commissaire à la transformation par ordonnance du Président du tribunal (sauf accord unanime des associés dans certains cas — L.223-43), (ii) une publication au JAL, (iii) une inscription modificative au RCS dans le mois. Pour SAS→SA : capital minimum 37 000 € et rapport CAC obligatoire.",
    },
  },

  dissolution: {
    ordre_du_jour: [
      {
        label: "Dissolution avec liquidateur amiable",
        description: "Cas standard — SARL, SAS, SA",
        items: [
          "Rapport du [Gérant/Président] sur les motifs de la dissolution",
          "Dissolution anticipée de la société",
          "Ouverture de la liquidation amiable",
          "Nomination du Liquidateur et fixation de ses pouvoirs",
          "Fixation du siège de liquidation",
          "Fixation de la rémunération du Liquidateur",
          "Pouvoirs pour l'accomplissement des formalités de publicité",
        ],
      },
      {
        label: "TUP (associé unique — L.1844-5)",
        description: "Transmission Universelle de Patrimoine sans liquidation",
        items: [
          "Constatation que la société a un associé unique personne morale",
          "Dissolution anticipée avec TUP en application de l'article 1844-5 du Code civil",
          "Transmission universelle du patrimoine à l'associé unique",
          "Absence de nomination d'un liquidateur",
          "Pouvoirs pour l'accomplissement des formalités et de la publication",
        ],
      },
    ],
    contenu_resolution: [
      {
        label: "Dissolution + Liquidateur",
        description: "Dissolution amiable avec ouverture de la liquidation",
        content:
          "L'Assemblée Générale, statuant aux conditions requises pour la modification des statuts, après avoir pris connaissance du rapport du [Gérant/Président], décide la dissolution anticipée de la société à compter du [date]. La société subsiste pour les besoins de sa liquidation jusqu'à la clôture de celle-ci. Sa dénomination sociale sera suivie de la mention « société en liquidation » sur tous les actes et documents. L'Assemblée nomme en qualité de Liquidateur, pour toute la durée de la liquidation, Monsieur/Madame [Nom Prénom], demeurant [adresse]. Le Liquidateur dispose des pouvoirs les plus étendus pour réaliser l'actif, apurer le passif et procéder à toutes les opérations de liquidation conformément aux articles L.237-1 et suivants du Code de commerce. Le siège de liquidation est fixé à [adresse]. La rémunération du Liquidateur est fixée à [montant] euros / [rémunération à titre gratuit].",
      },
      {
        label: "TUP (art. 1844-5)",
        description: "Confusion de patrimoine — associé unique personne morale",
        content:
          "L'Associé Unique de la société, à savoir la société [Dénomination associé unique], [forme juridique] au capital de [montant] euros, immatriculée au RCS de [ville] sous le n° [SIREN], constate qu'il est l'unique associé de la société. En application de l'article 1844-5 alinéa 3 du Code civil, l'Associé Unique décide la dissolution anticipée de la société [Dénomination société dissoute] sans liquidation. Cette dissolution entraînera la transmission universelle du patrimoine de la société à l'Associé Unique, à l'issue du délai d'opposition des créanciers de 30 jours à compter de la publication de la décision de dissolution au Bodacc. Aucun liquidateur n'est nommé. Tous pouvoirs sont donnés au représentant légal de l'Associé Unique pour effectuer les formalités de publicité et de radiation.",
      },
      {
        label: "Clôture de liquidation",
        description: "Constat de fin de liquidation, quitus au liquidateur",
        content:
          "L'Assemblée Générale, après avoir pris connaissance du rapport et des comptes de liquidation établis par le Liquidateur, approuve lesdits comptes tels qu'ils lui ont été présentés. L'Assemblée constate la clôture des opérations de liquidation à compter du [date]. Elle donne quitus entier et sans réserve au Liquidateur de sa gestion et le décharge de son mandat. Le solde de liquidation, s'élevant à [montant] euros, sera réparti entre les associés au prorata de leurs droits dans le capital [le cas échéant : selon les modalités suivantes : [détail]]. Tous pouvoirs sont donnés au porteur d'une copie du présent procès-verbal pour effectuer les formalités de radiation au RCS.",
      },
    ],
    date_effet: [
      { label: "Immédiat", value: "Immédiat", hint: "Effet dès le vote — la société entre en liquidation" },
      {
        label: "À l'issue du délai d'opposition (30 j — TUP)",
        value: "À l'issue du délai d'opposition des créanciers de 30 jours à compter de la publication au Bodacc",
        hint: "Obligatoire pour la TUP (art. 1844-5 al. 3 du Code civil)",
      },
    ],
    warning: {
      title: "Points de vigilance",
      body: "(i) dissolution amiable : nomination d'un liquidateur obligatoire, publication JAL sous 1 mois, dépôt au greffe. (ii) TUP : uniquement si associé unique personne morale (impossible si personne physique), délai d'opposition créanciers de 30 jours obligatoire avant la transmission effective du patrimoine. (iii) Les comptes de liquidation doivent être approuvés par une AGO ultérieure pour clôturer.",
    },
  },
};

function GenericResolutionForm({ data, onChange, ageType }) {
  const templates = RESOLUTION_TEMPLATES[ageType.id];

  const applyOrdreDuJour = (items) => onChange("ordre_du_jour", items);
  const applyResolutionContent = (content) =>
    onChange("resolutions_custom", [{ titre: "PREMIÈRE RÉSOLUTION", contenu: content }]);
  const applyDateEffet = (value) => onChange("date_effet", value);

  return (
    <div className="space-y-6 max-w-2xl mx-auto">
      {!templates && (
        <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <p className="text-sm text-yellow-800">
            Formulaire générique pour : <strong>{ageType.name}</strong>
          </p>
        </div>
      )}

      {/* Ordre du jour */}
      <div className="space-y-2">
        <Label>Ordre du jour</Label>

        {templates?.ordre_du_jour && (
          <div className="mb-2 space-y-2" data-testid="templates-ordre-du-jour">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Modèles prêts à l'emploi
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {templates.ordre_du_jour.map((tpl, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => applyOrdreDuJour(tpl.items)}
                  className="text-left p-3 border rounded-lg bg-white hover:bg-blue-50 hover:border-blue-300 transition text-xs"
                  data-testid={`template-ordre-${i}`}
                >
                  <p className="font-semibold text-foreground">{tpl.label}</p>
                  <p className="text-muted-foreground mt-0.5">{tpl.description}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        <Textarea
          value={(data.ordre_du_jour || []).join("\n")}
          onChange={(e) => onChange("ordre_du_jour", e.target.value.split("\n").filter(Boolean))}
          placeholder="Un point par ligne..."
          rows={5}
          data-testid="ordre-du-jour-textarea"
        />
      </div>

      {/* Contenu de la résolution */}
      <div className="space-y-2">
        <Label>Contenu de la résolution</Label>

        {templates?.contenu_resolution && (
          <div className="mb-2 space-y-2" data-testid="templates-contenu-resolution">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Exemples cliquables
            </p>
            <div className="space-y-2">
              {templates.contenu_resolution.map((tpl, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => applyResolutionContent(tpl.content)}
                  className="w-full text-left p-3 border rounded-lg bg-white hover:bg-blue-50 hover:border-blue-300 transition"
                  data-testid={`template-resolution-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1">
                      <p className="text-xs font-semibold text-foreground">{tpl.label}</p>
                      <p className="text-[10px] text-muted-foreground">{tpl.description}</p>
                    </div>
                    <span className="text-[10px] text-blue-600 font-medium flex-shrink-0">Insérer →</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-2 line-clamp-2 italic">
                    {tpl.content.substring(0, 180)}...
                  </p>
                </button>
              ))}
            </div>
          </div>
        )}

        <Textarea
          value={data.resolutions_custom?.[0]?.contenu || ""}
          onChange={(e) => onChange("resolutions_custom", [{
            titre: "PREMIÈRE RÉSOLUTION",
            contenu: e.target.value
          }])}
          placeholder="Décrivez la résolution..."
          rows={8}
          data-testid="resolution-content-textarea"
        />

        {templates?.contenu_resolution && (
          <p className="text-[11px] text-muted-foreground italic">
            Après insertion : remplace les [crochets] par tes valeurs (montants, dates, article statuts…).
          </p>
        )}
      </div>

      {/* Date d'effet */}
      <div className="space-y-2">
        <Label>Date d&apos;effet</Label>

        {templates?.date_effet && (
          <div className="flex flex-wrap gap-2 mb-2" data-testid="templates-date-effet">
            {templates.date_effet.map((tpl, i) => (
              <button
                key={i}
                type="button"
                onClick={() => applyDateEffet(tpl.value)}
                className="px-3 py-1.5 text-xs border rounded-full bg-white hover:bg-blue-50 hover:border-blue-300 transition"
                title={tpl.hint}
                data-testid={`template-date-effet-${i}`}
              >
                {tpl.label}
              </button>
            ))}
          </div>
        )}

        <Input
          value={data.date_effet || ""}
          onChange={(e) => onChange("date_effet", e.target.value)}
          placeholder="Immédiat ou date précise (01/09/2026)"
          data-testid="date-effet-input"
        />
      </div>

      {/* Point d'attention dynamique selon le type d'AGE */}
      {templates?.warning && (
        <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
          <span className="text-lg leading-none">⚠️</span>
          <p>
            <strong>{templates.warning.title} :</strong> {templates.warning.body}
          </p>
        </div>
      )}
    </div>
  );
}
